import React, { useEffect } from 'react';
import {
  Navigate,
  RouteObject,
  createBrowserRouter,
  RouterProvider
} from 'react-router-dom';
import { clarity } from 'react-microsoft-clarity';

import { compose } from 'recompose';
import { connect } from 'react-redux';
import { userDuck } from '../ducks';
import CompleteSignup from 'pages/login-v2/complete-signup';
import LoginPageLayout from 'pages/login-v2/login-page-layout';
import MainLayoutRoute from './mainLayoutRoute';
import LoginV2 from 'pages/login-v2/login-page-v2';
import ResetPasswordV2 from 'pages/login-v2/reset-password-v2';
import ForgotPasswordV2 from 'pages/login-v2/forgot-password-v2';
import { useAuth } from '_clean-code/api/hooks/useAuth';
import posthog from 'posthog-js';
import { I18n } from '@utils/i18n';

interface AppRouteProps {
  userData: any;
}

const AuthWrapper: React.FC<{ element: React.ReactNode }> = ({ element }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{element}</> : <Navigate to="/login" />;
};

const NonAuthWrapper: React.FC<{ element: React.ReactNode }> = ({
  element
}) => {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? (
    <>{element}</>
  ) : (
    <Navigate to="/dashboard" replace />
  );
};

const createRoutes = (): RouteObject[] => [
  {
    path: '/login',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <LoginV2 />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/forgot-password',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <ForgotPasswordV2 />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/reset-password',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <ResetPasswordV2 />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/invite/accept',
    element: (
      <NonAuthWrapper
        element={
          <LoginPageLayout>
            <CompleteSignup />
          </LoginPageLayout>
        }
      />
    )
  },
  {
    path: '/*',
    element: <AuthWrapper element={<MainLayoutRoute />} />
  }
];

const AppRoutes = ({ userData }: AppRouteProps) => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      window.analytics?.reset();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && userData?.email) {
      if (userData?.email !== 'user@example.com') {
        posthog.identify(userData.email, {
          email: userData?.email,
          companyId: userData?.companyId,
          name: userData?.name,
          language: I18n._locale,
          role: userData?.role
        });
      }
      if (clarity.hasStarted()) {
        clarity.consent();
        clarity.identify(userData.email, { email: userData.email });
        clarity.setTag('email', userData.email);
        /* istanbul ignore next */
        if (userData?.companyId) {
          clarity.setTag('companyId', String(userData.companyId));
        }
      }
    }
  }, [isAuthenticated, userData]);

  const router = createBrowserRouter(createRoutes());

  return <RouterProvider router={router} />;
};

const mapStateToProps = (state) => ({
  userData: userDuck.selectors.userDataSelector(state),
  user: state.user
});

export default compose(connect(mapStateToProps))(AppRoutes as any);
